import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { emailAction, auth as authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, XCircle, ShieldAlert, Ban } from "lucide-react";
import { toast } from "sonner";

/**
 * `/action/:voc?do=approve|reject&t=<jwt>` — landing page hit by email button clicks.
 *
 * Flow:
 *   1. On mount, POST /email-action/preview with the JWT to learn:
 *        - status: "ready" (device cookie matches → one-click OK),
 *                  "login_required" (cookie missing/mismatched → prompt login),
 *                  "already_done" (link already used or PR actioned).
 *        - recipient name / PR supplier / amount / stage / action.
 *   2. If ready + approve → single big "Confirm Approve" button.
 *      If ready + reject → same layout with a required reason textarea.
 *      If login_required → send user to /login?redirect=<current URL>.
 *      If already_done → show read-only success/info card.
 */
export default function EmailAction() {
  const { voc } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("t") || "";
  const doAction = (params.get("do") || "").toLowerCase();  // preview also returns this
  const [state, setState] = useState({ loading: true });
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setState({ loading: false, error: "Missing action token." }); return; }
      try {
        const { data } = await emailAction.preview(token);
        if (!cancelled) setState({ loading: false, ...data });
      } catch (e) {
        if (!cancelled) setState({
          loading: false,
          error: e?.response?.data?.detail || "Could not verify this action link.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await emailAction.confirm(token, reason);
      toast.success(data?.message || `PR #${voc} ${doAction}d`);
      setState((s) => ({ ...s, status: "done", executed: true, message: data?.message }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply action");
    } finally {
      setSubmitting(false);
    }
  };

  const goLogin = () => {
    const back = encodeURIComponent(window.location.pathname + window.location.search);
    nav(`/login?redirect=${back}`);
  };

  if (state.loading) {
    return (
      <Shell>
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Verifying your action link…</span>
        </div>
      </Shell>
    );
  }

  if (state.error) {
    return (
      <Shell>
        <BigIcon variant="warn" />
        <h1 className="text-2xl font-semibold text-slate-900 mt-6">Link problem</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-md">{state.error}</p>
        <Button onClick={() => nav("/home")} className="mt-6" data-testid="email-action-home-btn">
          Open Smart Approvals
        </Button>
      </Shell>
    );
  }

  if (state.status === "already_done" || state.status === "done") {
    const wasDo = (state.do || doAction || "").toLowerCase();
    const actionedBy = state.already_by;
    const at = state.already_at ? new Date(state.already_at).toLocaleString() : null;
    return (
      <Shell>
        <BigIcon variant={wasDo === "reject" ? "danger" : "success"} />
        <h1 className="text-2xl font-semibold text-slate-900 mt-6" data-testid="email-action-done-title">
          PR #{voc} — {state.executed ? `${wasDo}d` : (state.already_status || "already actioned")}
        </h1>
        {state.message ? (
          <p className="text-sm text-slate-600 mt-2 max-w-md">{state.message}</p>
        ) : actionedBy ? (
          <p className="text-sm text-slate-600 mt-2 max-w-md">
            Actioned by <b className="text-slate-900">{actionedBy}</b>{at ? ` on ${at}` : ""}. Nothing to do here.
          </p>
        ) : (
          <p className="text-sm text-slate-600 mt-2 max-w-md">This action link has already been used.</p>
        )}
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={() => nav(`/pipeline?voc=${voc}`)} data-testid="email-action-open-pr">Open PR</Button>
          <Button onClick={() => nav("/home")} data-testid="email-action-home-btn">Home</Button>
        </div>
      </Shell>
    );
  }

  if (state.status === "login_required") {
    return (
      <Shell>
        <BigIcon variant="warn" />
        <h1 className="text-2xl font-semibold text-slate-900 mt-6">Log in to continue</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-md">
          This link was sent to <b className="text-slate-900">{state.recipient_name || "the assigned reviewer"}</b>.
          Because you&apos;re opening it on a browser that hasn&apos;t been signed in as them,
          we need a quick login before applying the action.
        </p>
        <p className="text-xs text-slate-500 mt-3 max-w-md">
          This is what protects forwarded emails — only the intended recipient can act.
        </p>
        <Button onClick={goLogin} className="mt-6" data-testid="email-action-login-btn">
          Log in as {state.recipient_name || "recipient"}
        </Button>
      </Shell>
    );
  }

  // status === "ready" — device cookie matched, one-click UI
  const isApprove = (state.do || doAction) === "approve";
  const stageLabel = state.stage === "requestor" ? "Requestor" : "L1 Authoriser";
  return (
    <Shell>
      <BigIcon variant={isApprove ? "success" : "danger"} />
      <div className="mt-6 text-xs uppercase tracking-widest text-slate-500">
        {stageLabel} · {isApprove ? "Approve" : "Reject"}
      </div>
      <h1 className="text-2xl font-semibold text-slate-900 mt-2" data-testid="email-action-title">
        {isApprove ? "Approve" : "Reject"} PR #{voc}?
      </h1>
      <div className="mt-3 text-sm text-slate-600 max-w-md">
        {state.recipient_name ? <>Hi <b className="text-slate-900">{state.recipient_name}</b>. </> : null}
        {state.supplier ? <>Supplier: <b className="text-slate-900">{state.supplier}</b>. </> : null}
        {state.amount ? <>Amount: <b className="text-slate-900">₹{Number(state.amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>.</> : null}
      </div>

      {!isApprove && (
        <div className="w-full max-w-md mt-6">
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">
            Reason for rejection <span className="text-rose-600">*</span>
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder="e.g. Price higher than quotation, wrong quantity, no longer required…"
            className="mt-2"
            disabled={submitting}
            data-testid="email-action-reason"
          />
          <div className="text-[11px] text-slate-500 mt-1 text-right">{reason.length}/500</div>
        </div>
      )}

      <div className="flex gap-3 mt-6">
        <Button
          variant="outline"
          onClick={() => nav(`/pipeline?voc=${voc}`)}
          disabled={submitting}
          data-testid="email-action-cancel"
        >
          Open in app instead
        </Button>
        <Button
          onClick={submit}
          disabled={submitting || (!isApprove && reason.trim().length < 5)}
          className={
            isApprove
              ? "bg-emerald-600 hover:bg-emerald-500 text-white"
              : "bg-rose-600 hover:bg-rose-500 text-white"
          }
          data-testid="email-action-confirm"
        >
          {submitting ? <Loader2 size={14} className="mr-1 animate-spin" /> :
            isApprove ? <CheckCircle2 size={14} className="mr-1" /> : <Ban size={14} className="mr-1" />}
          {submitting ? "Applying…" : `Confirm ${isApprove ? "Approve" : "Reject"}`}
        </Button>
      </div>

      <p className="text-[11px] text-slate-400 mt-8 max-w-md leading-relaxed">
        You&apos;re signed in on this browser, so no password is needed to confirm.
        Links expire in 7 days and can be used only once.
      </p>
    </Shell>
  );
}


function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 flex flex-col items-center text-center">
        {children}
      </div>
    </div>
  );
}

function BigIcon({ variant }) {
  const cls = {
    success: "bg-emerald-100 text-emerald-700",
    danger: "bg-rose-100 text-rose-700",
    warn: "bg-amber-100 text-amber-700",
  }[variant] || "bg-slate-100 text-slate-600";
  const Icon = { success: CheckCircle2, danger: XCircle, warn: ShieldAlert }[variant] || ShieldAlert;
  return (
    <div className={`w-16 h-16 rounded-full ${cls} flex items-center justify-center`}>
      <Icon size={30} />
    </div>
  );
}
